// Listener analytics for audio journals: events sent through the app's telemetry (logEvent → Firebase /
// Google Analytics), named and shaped so GA reports and BigQuery queries can be built directly from them.
// Catalog, GA custom definitions and sample queries: audio_journal/docs/sas-app-publishing.md#listener-analytics
import { Article, AudioChapter } from 'types';
import { chapterIndexAt } from './audioJournal';

export type AnalyticsEvent = [name: string, params: Record<string, string | number>];

/** Which issue an event is about (every audio journal event carries these). */
export function audioJournalParams(article: Article) {
	const info = article.audioJournal!;
	return {
		edition_id: info.editionId,
		publication_id: info.publicationId,
		publication: (article.group ?? '').slice(0, 100),
		issue: info.issue,
		language: article.lang,
		version: info.version,
	};
}

/** Where listening was started from, by the app's route at the time. */
export function entryPoint(pathname: string, search: string) {
	const first = pathname.split('/').filter(Boolean)[0] ?? '';
	if (first === 'audiojournal') {
		return new URLSearchParams(search).get('src') === 'push' ? 'push' : 'link';
	}
	return ({ '': 'home', publication: 'reader', publications: 'publications' } as Record<string, string>)[first] ?? first;
}

/** A listener moving to a chapter or passage (chapter list, previous/next, a tapped passage). */
export function navigateEvent(article: Article, via: string, time: number): AnalyticsEvent {
	const chapters = article.audioJournal!.chapters;
	return ['audio_journal_navigate', { ...audioJournalParams(article), via, to_chapter: chapterIndexAt(chapters, time) + 1 }];
}

const HEARTBEAT_S = 60;
const CHAPTER_HEARD = 0.5; // a chapter counts as listened once half of it was heard

/**
 * Turns playback position reports of one issue into listening events: time spent listening (every minute and
 * when playback pauses or stops), chapters listened (half of a chapter heard by playing it, not by skipping) and
 * reaching the end. Seeks and skips are not counted as listening.
 */
export class ListeningTracker {
	readonly id: string;
	private params: ReturnType<typeof audioJournalParams>;
	private chapters: AudioChapter[];
	private duration: number;
	private last?: { time: number; at: number };
	private pendingWall = 0;
	private pendingMedia = 0;
	private totalWall = 0;
	private heard: number[];
	private counted = new Set<number>();
	private completed = false;

	constructor(article: Article) {
		this.id = article.id;
		this.params = audioJournalParams(article);
		this.chapters = article.audioJournal!.chapters;
		this.duration = article.mediaLength ?? 0;
		this.heard = this.chapters.map(() => 0);
	}

	/** A playback position (s) at a moment (ms); returns the events it completes. */
	sample(time: number, at: number, playing: boolean, speed = 1): AnalyticsEvent[] {
		const last = this.last;
		this.last = playing ? { time, at } : undefined;
		if (!last || !playing) {
			return [];
		}
		const wall = (at - last.at) / 1000;
		const media = time - last.time;
		// Playing on at 0.5–2x, also across gaps while the app was in the background; anything else is a seek.
		if (!(wall > 0 && media > 0 && media <= wall * 2.2 + 2 && media >= wall * 0.4 - 2)) {
			return [];
		}
		this.pendingWall += wall;
		this.pendingMedia += media;
		this.totalWall += wall;

		const events: AnalyticsEvent[] = [];
		this.chapters.forEach((chapter, index) => {
			const overlap = Math.min(time, chapter.end) - Math.max(last.time, chapter.start);
			if (overlap <= 0) {
				return;
			}
			this.heard[index] += overlap;
			if (!this.counted.has(index) && this.heard[index] >= (chapter.end - chapter.start) * CHAPTER_HEARD) {
				this.counted.add(index);
				events.push([
					'audio_journal_chapter',
					{
						...this.params,
						chapter: index + 1,
						chapter_count: this.chapters.length,
						chapter_title: chapter.title.slice(0, 100),
						article_id: chapter.articleId,
					},
				]);
			}
		});
		if (!this.completed && this.duration > 0 && time >= this.duration - 10) {
			this.completed = true;
			events.push([
				'audio_journal_complete',
				{ ...this.params, listened_seconds: Math.round(this.totalWall), chapters_listened: this.counted.size, chapter_count: this.chapters.length },
			]);
		}
		if (this.pendingWall >= HEARTBEAT_S) {
			events.push(...this.flush(speed));
		}
		return events;
	}

	/** Listening time not yet reported (on pause, stop, leaving the app or another track). */
	flush(speed = 1): AnalyticsEvent[] {
		const seconds = Math.round(this.pendingWall);
		const media = Math.round(this.pendingMedia);
		this.pendingWall = 0;
		this.pendingMedia = 0;
		if (seconds < 1) {
			return [];
		}
		const chapter = this.last ? chapterIndexAt(this.chapters, this.last.time) + 1 : 0;
		return [['audio_journal_listen', { ...this.params, seconds, media_seconds: media, chapter, speed }]];
	}

	/** Playback paused or stopped: report the time so far; the next report starts afresh. */
	pause(speed = 1): AnalyticsEvent[] {
		const events = this.flush(speed);
		this.last = undefined;
		return events;
	}
}
